<template>
  <div class="audio-booth">
    <header class="panel-header">
      <div class="panel-left-item">
        <button class="text-button narrow-button" @click="close">{{ __('Close') }}</button>
      </div>
      <div class="panel-title">{{ __('Audio booth') }}</div>
      <div class="panel-right-item">
        <button v-show="hasChunks && !recording" class="text-button narrow-button" @click="done">
          Playback and save
        </button>
      </div>
    </header>
    <div class="panel-body" :style="{ height: height + 'px' }">
      <div v-show="!streaming" class="panel-interstitial tv">
        <div class="tv-cell">
          <OzLoadingSpinner />
        </div>
        <div class="tv-cell">
          <p>{{ __('Starting microphone ...') }}</p>
          <p class="caption-type">{{ __('Make sure you have given us permission to use your microphone.') }}</p>
        </div>
      </div>
      <audio-visualizer
        v-if="streaming"
        :width="width"
        :height="height"
        :frequencies="currentVolume"
        :bar-color="visualizationColor"
      />
      <audio-instructions v-if="streaming && !recording && !hasChunks" />
      <div class="audio-booth-meta">
        <div
          v-if="recording || secondsElapsed > 0"
          :class="{
            'display3-type': true,
            'caption-type': !recording,
            'error-type': recording && isReachingRecordingLimit,
          }"
        >
          {{ recordingDurationText }}
        </div>
        <div v-show="recording" class="audio-booth-beacon">
          <span class="audio-booth-beacon-text">Rec</span><base-icon name="record" />
        </div>
        <div v-show="!recording && hasChunks && !hasReachedRecordingLimit" class="audio-booth-beacon caption-type">
          {{ __('Paused') }}
        </div>
        <div v-show="hasReachedRecordingLimit" class="audio-booth-beacon caption-type">
          Sorry, you've run out of time.
        </div>
      </div>
    </div>
    <footer v-show="!recording" class="panel-footer">
      <div>
        <div v-if="audioDevices.length > 1" class="select-box">
          <select @change="chooseAudioDevice">
            <option
              v-for="audioDevice in audioDevices"
              :key="audioDevice.id"
              :selected="audioDevice.id == activeAudioDeviceId"
              :value="audioDevice.id"
            >
              {{ audioDevice.label }}
            </option>
          </select>
        </div>
      </div>
      <button
        v-show="streaming && !hasReachedRecordingLimit"
        class="floating-action-button shadow-z2 hover-hover:hover:shadow-z3"
        :title="recordingButtonTitle"
        @click="startRecording"
      >
        <base-icon name="mic" />
      </button>
      <button
        v-show="hasChunks"
        class="text-button narrow-button"
        :title="__('Start from scratch')"
        @click="restartRecording"
      >
        {{ __('Discard and restart') }}
      </button>
    </footer>
    <footer v-show="recording" class="panel-footer">
      <button class="floating-action-button audio-booth-pause" :title="__('Pause recording')" @click="pauseRecording">
        <base-icon name="pause" />
      </button>
    </footer>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import OzLoadingSpinner from 'library/v4/components/OzLoadingSpinner.vue'
import AudioVisualizer from 'vuecomponents/audio_visualizer'
import AudioInstructions from 'vuecomponents/audio_instructions'
import BaseIcon from 'vuecomponents/base_icon'
import AVRecorderMixin from 'vuemixins/av_recorder_mixin'
import { trackEvent } from 'bits/analytics'

export default {
  components: {
    AudioVisualizer,
    BaseIcon,
    OzLoadingSpinner,
    AudioInstructions,
  },
  mixins: [AVRecorderMixin],
  props: ['width', 'height'],
  data() {
    return {
      currentVolume: [],
      maxDuration: 900, // in seconds
      bufferDuration: 15,
    }
  },
  computed: {
    constraints() {
      return {
        audio: {
          deviceId: this.activeAudioDeviceId ? { exact: this.activeAudioDeviceId } : undefined,
        },
      }
    },
    isReachingRecordingLimit() {
      return this.secondsElapsed >= this.maxDuration - 60
    },
    visualizationColor() {
      if (this.recording) {
        return '#FEC006'
      }
      return '#E3E3E3'
    },
  },
  mounted() {
    this.mountStreaming()
    this.mountRecording('audio')
  },
  beforeDestroy() {
    this.stopAudioProcessor()
    this.stopRecording()
    this.stopStreaming()
  },
  methods: {
    close() {
      this.$emit('close')
    },
    resetCanvas() {
      const canvas = this.$refs.canvas
      const context = canvas.getContext('2d')
      context.fillStyle = 'black'
      context.fillRect(0, 0, canvas.width, canvas.height)
    },
    async done() {
      /**
       * When you are done, there are two possibilities:
       * 1. We already have recording data, in which case, just emit the event.
       * 2. We don't have data and we need to wait for recording data to be available
       *    before we fire the done event.
       *    (When recording data is available, gotRecordingData is automatically called.)
       */
      trackEvent('Audio Booth', 'Recorded', null, this.secondsElapsed)
      if (this.recordingStatus == 'stopped') {
        const blob = await this.recordedBlob()
        this.$emit('done', blob)
      } else {
        this.stopRecording()
      }
    },
    async gotRecordedData() {
      const blob = await this.recordedBlob()
      this.$emit('done', blob)
    },
    gotStream(stream) {
      // Handle the incoming audio stream
      this.stopAudioProcessor()
      const AudioContext = window.AudioContext || window.webkitAudioContext
      this._audioContext = new AudioContext()
      this._input = this._audioContext.createMediaStreamSource(stream)
      this._analyser = this._audioContext.createAnalyser()
      this._scriptProcessor = this._audioContext.createScriptProcessor()

      // Some analyser setup
      this._analyser.smoothingTimeConstant = 0.3
      this._analyser.fftSize = 256

      this._input.connect(this._analyser)
      this._analyser.connect(this._scriptProcessor)
      this._scriptProcessor.connect(this._audioContext.destination)

      this._scriptProcessor.onaudioprocess = () => {
        if (this._analyser) {
          // it is possible that analyser is null
          const tempArray = new Uint8Array(128)
          this._analyser.getByteFrequencyData(tempArray)
          this.currentVolume = Array.from(tempArray)
        }
      }
    },
    stopAudioProcessor() {
      if (this._audioContext) {
        this._scriptProcessor.disconnect(this._audioContext.destination)
        this._analyser.disconnect(this._scriptProcessor)
        this._input.disconnect(this._analyser)
        this._audioContext.close()
        this._audioContext = null
        this._scriptProcessor = null
        this._analyser = null
        this._input = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
.panel-body {
  position: relative;
}
.audio-booth-meta {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.audio-booth-beacon {
  display: flex;
  align-items: center;
  height: 24px;
  i {
    //the symbol is not aligned.
    margin-top: -2px;
    margin-left: 4px;
    color: $red-color;
  }
}
.audio-booth-beacon-text {
  line-height: 24px;
  font-size: 13px;
  text-transform: uppercase;
}
.floating-action-button {
  position: absolute;
  top: -28px;
  left: calc(50% - 28px);
}
footer {
  position: relative;
  .icon-button {
    @include directional-margin($margin-left: -2px, $margin-right: 0);
  }
}
select {
  max-width: 232px;
}
</style>
